export interface LocationSuggestion {
  /** Valeur normalisée finale, ex. "Paris (75001)". */
  label: string;
  city: string;
  postcode: string;
  /** Ex. "75, Paris, Île-de-France" — affiché en secondaire dans le dropdown. */
  context: string;
}

interface BanFeature {
  properties?: {
    city?: string;
    postcode?: string;
    context?: string;
    label?: string;
  };
}

const BAN_URL = 'https://api-adresse.data.gouv.fr/search/';

/** Mapping pur réponse BAN → suggestion. Retourne null si la feature est inexploitable. */
export function mapBanFeatureToSuggestion(feature: BanFeature): LocationSuggestion | null {
  const p = feature?.properties;
  if (!p?.city || !p?.postcode) return null;
  return {
    label: `${p.city} (${p.postcode})`,
    city: p.city,
    postcode: p.postcode,
    context: p.context ?? '',
  };
}

export function useLocationAutocomplete() {
  let controller: AbortController | null = null;

  async function search(query: string): Promise<LocationSuggestion[]> {
    const q = query.trim();
    if (q.length < 2) return [];

    // Annule toute requête en vol pour qu'un résultat obsolète n'écrase jamais le plus récent.
    controller?.abort();
    controller = new AbortController();

    try {
      const data = await $fetch<{ features?: BanFeature[] }>(BAN_URL, {
        query: { q, type: 'municipality', limit: 5, autocomplete: 1 },
        signal: controller.signal,
      });
      return (data.features ?? [])
        .map(mapBanFeatureToSuggestion)
        .filter((s): s is LocationSuggestion => s !== null);
    } catch {
      // Requête annulée OU échec réseau/API → se comporte comme "aucune suggestion".
      // On ne throw jamais : le champ doit rester utilisable en input texte simple.
      return [];
    }
  }

  return { search };
}
